import { createContext, useCallback, useContext, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { ModelService } from "@/services/ModelService";
import { CandidateService } from "@/services/CandidateService";

const RECRUITMENTS_TO_SAMPLE = 5;
const CANDIDATES_PER_RECRUITMENT_SAMPLE = 10;

const RecruitmentDashboardDataContext = createContext(null);

// Lives above the routes (mounted once in Layout) so the recruitment dashboard's data
// survives navigating away to /sales and back -- without this, switching workspaces would
// unmount RecruitmentDashboardPage and re-run its whole fetch every time.
export const RecruitmentDashboardDataProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [state, setState] = useState({
        status: "idle", // idle -> loading -> loaded
        activeRecruitments: [],
        activeTotalCount: 0,
        sampledCandidates: [],
        metrics: { newCandidates: 0, toReview: 0, favourites: 0 },
    });

    const load = useCallback(() => {
        if (!currentUser?.id || state.status !== "idle") return;
        setState((prev) => ({ ...prev, status: "loading" }));

        const metricsPromise = CandidateService.getOwnerActiveMetrics(currentUser.id)
            .catch(() => null);

        ModelService.getActiveWorkflowsByOwner(currentUser.id, 1, 20)
            .then(async (workflowsResponse) => {
                const items = workflowsResponse?.items ?? [];
                const sortedByRecent = [...items].sort(
                    (a, b) => new Date(b.updatedAtUtc) - new Date(a.updatedAtUtc)
                );
                const sample = sortedByRecent.slice(0, RECRUITMENTS_TO_SAMPLE);
                const candidateResults = await Promise.allSettled(
                    sample.map((recruitment) =>
                        CandidateService.getByRecruitment(recruitment.id, 1, CANDIDATES_PER_RECRUITMENT_SAMPLE, [], null, "createdat", "desc")
                            .then((res) => ({ recruitment, res }))
                    )
                );

                const recruitmentsWithCounts = sample.map((recruitment, index) => {
                    const result = candidateResults[index];
                    const totalCount = result?.status === "fulfilled" ? result.value.res?.totalCount ?? null : null;
                    return { ...recruitment, candidateCount: totalCount };
                });

                const flatCandidates = candidateResults
                    .filter((result) => result.status === "fulfilled")
                    .flatMap(({ value: { recruitment, res } }) =>
                        (res?.items ?? []).map((candidate) => ({ candidate, recruitment }))
                    );

                const metricsResponse = await metricsPromise;

                setState({
                    status: "loaded",
                    activeRecruitments: recruitmentsWithCounts,
                    activeTotalCount: workflowsResponse?.totalCount ?? items.length,
                    sampledCandidates: flatCandidates,
                    metrics: {
                        newCandidates: metricsResponse?.newCandidates ?? 0,
                        toReview: metricsResponse?.toReview ?? 0,
                        favourites: metricsResponse?.favourites ?? 0,
                    },
                });
            })
            .catch(() => setState((prev) => ({ ...prev, status: "loaded" })));
    }, [currentUser?.id, state.status]);

    const value = {
        ...state,
        isLoading: state.status !== "loaded",
        load,
    };

    return (
        <RecruitmentDashboardDataContext.Provider value={value}>
            {children}
        </RecruitmentDashboardDataContext.Provider>
    );
};

export const useRecruitmentDashboardData = () => useContext(RecruitmentDashboardDataContext);
